/**
 * Outreach sequence runner — the PURE due-step engine + analytics.
 *
 * This is the deterministic heart of the outreach module. It answers two
 * questions with zero side effects, no clocks, no store, no sending:
 *
 *   1. `dueSteps(sequence, enrollments, now)` — which enrollments have a
 *      step due to send right now?
 *   2. `sequenceStats(sequence, enrollments)` — honest funnel + reply rate.
 *
 * The runner AGENT (src/lib/agent/sequenceAgent.ts) turns each due pair into
 * a `send_sequence_step` proposal in the approval queue — nothing sends
 * without a human. The executor (src/lib/agent/actions.ts) advances the
 * enrollment on approval. Both lean on the pure functions here so the whole
 * loop stays testable and honest.
 */

import type { OutreachSequence, OutreachEnrollment } from '../db/types';
import type { SequenceStep } from './outreach';

const DAY_MS = 86_400_000;

/** Accepts ISO strings, epoch ms, or Dates; NaN on garbage. */
function toMs(t: string | number | Date): number {
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  return Date.parse(t);
}

/** A sequence is eligible for sending when active (or legacy status-less). */
export function isSequenceActive(sequence: OutreachSequence): boolean {
  return sequence.status === undefined || sequence.status === 'active';
}

/** One (enrollment, step) pair the runner should propose a send for. */
export interface DueStep {
  enrollment: OutreachEnrollment;
  step: SequenceStep;
  /** Index of `step` within the sequence's steps array. */
  stepIndex: number;
}

/**
 * The enrollments whose NEXT step is due to send at `now`.
 *
 * A step is due once `enrolledAt + dayOffset days <= now`. An enrollment's
 * next step is `steps[currentStep]` — the executor advances `currentStep`
 * exactly once per approved send, so a step "already sent" is one below
 * `currentStep` and is never re-proposed. Enrollments that are stopped,
 * completed, past the last step, or whose sequence isn't active are
 * excluded. Same-day enrollment (dayOffset 0) is due immediately.
 *
 * Pure: no IO, inputs untouched. Garbage timestamps yield "not due".
 */
export function dueSteps(
  sequence: OutreachSequence,
  enrollments: OutreachEnrollment[],
  now: string | number | Date,
): DueStep[] {
  if (!isSequenceActive(sequence)) return [];
  const at = toMs(now);
  if (Number.isNaN(at)) return [];

  const out: DueStep[] = [];
  for (const enrollment of enrollments) {
    if (enrollment.sequenceId !== sequence.id) continue;
    if (enrollment.status !== 'active') continue;

    const idx = enrollment.currentStep;
    if (idx < 0 || idx >= sequence.steps.length) continue;
    const step = sequence.steps[idx];

    const start = toMs(enrollment.enrolledAt);
    if (Number.isNaN(start)) continue;

    const dueAt = start + step.dayOffset * DAY_MS;
    if (dueAt > at) continue;

    out.push({ enrollment, step, stepIndex: idx });
  }
  return out;
}

// --- Analytics -------------------------------------------------------------

/** Honest funnel for one sequence. `replyRate` is null when nobody's enrolled. */
export interface SequenceStats {
  enrolled: number;
  active: number;
  completed: number;
  stopped: number;
  /**
   * Fraction (0–1) of enrollments stopped explicitly because they replied.
   * NULL when `enrolled` is 0 — we never fabricate a 0% reply rate out of
   * an empty sequence. The UI renders null as "—".
   */
  replyRate: number | null;
}

/**
 * Roll up enrollment outcomes for one sequence. Only counts enrollments
 * that reference `sequence.id`. `replyRate` counts ONLY enrollments whose
 * `stoppedReason === 'replied'` — an explicit, human-marked signal, never
 * inferred — over the total enrolled. Null when there is no data.
 *
 * Pure: no IO, inputs untouched.
 */
export function sequenceStats(
  sequence: OutreachSequence,
  enrollments: OutreachEnrollment[],
): SequenceStats {
  const mine = enrollments.filter((e) => e.sequenceId === sequence.id);
  const enrolled = mine.length;
  let active = 0;
  let completed = 0;
  let stopped = 0;
  let replied = 0;
  for (const e of mine) {
    if (e.status === 'active') active++;
    else if (e.status === 'completed') completed++;
    else if (e.status === 'stopped') {
      stopped++;
      if (e.stoppedReason === 'replied') replied++;
    }
  }
  return {
    enrolled,
    active,
    completed,
    stopped,
    replyRate: enrolled === 0 ? null : replied / enrolled,
  };
}

// --- Template substitution -------------------------------------------------

/**
 * Fill `{{firstName}}` / `{{name}}` (and `{{company}}`) tokens in a subject
 * or body from a contact. Unknown tokens are left verbatim so a typo never
 * silently drops content. `{{firstName}}` is the first whitespace-delimited
 * word of the full name. Case-insensitive token names; whitespace tolerant.
 *
 * Pure: returns a new string.
 */
export function fillTemplate(
  template: string,
  contact: { name?: string; company?: string },
): string {
  const first = (contact.name ?? '').trim().split(/\s+/)[0] ?? '';
  const values: Record<string, string> = {
    firstname: first,
    name: contact.name ?? '',
    company: contact.company ?? '',
  };
  return template.replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (whole, token: string) => {
    const key = token.toLowerCase();
    return key in values ? values[key] : whole;
  });
}
